import React, { useState, useEffect, useRef } from 'react';
import { AuroraBackground, StarField, ParticleNet, CustomCursor, FlyingBat, CastleSilhouette, SideNav, GeoDivider } from './shared/Effects';
import { Link } from 'react-router-dom';

/* ─── HOOKS ─────────────────────────────────── */

const useScrollProgress = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const fn = () => {
      const el = document.documentElement;
      setP((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100 || 0);
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return p;
};

const useScrollY = () => {
  const [y, setY] = useState(0);
  useEffect(() => {
    const fn = () => setY(window.scrollY);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return y;
};

const useActiveSection = (ids) => {
  const [active, setActive] = useState('');
  useEffect(() => {
    const obs = [];
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const o = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) setActive(id); },
        { threshold: 0.25, rootMargin: '-80px 0px -30% 0px' }
      );
      o.observe(el);
      obs.push(o);
    });
    return () => obs.forEach(o => o.disconnect());
  }, [ids]);
  return active;
};

const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold }
    );
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, [threshold]);
  return [ref, vis];
};

const useCountUp = (target, active, duration = 1800) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let cur = 0;
    const step = target / (duration / 16);
    const t = setInterval(() => {
      cur += step;
      if (cur >= target) { setN(target); clearInterval(t); }
      else setN(Math.floor(cur));
    }, 16);
    return () => clearInterval(t);
  }, [active, target, duration]);
  return n;
};

/* ─── ANIMATED SECTION ───────────────────────── */

const FadeIn = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ref, vis] = useInView();
  const from = { up: 'translateY(36px)', left: 'translateX(-36px)', right: 'translateX(36px)', scale: 'scale(0.94) translateY(20px)' };
  return (
    <div ref={ref} style={{
      ...style,
      opacity: vis ? 1 : 0,
      transform: vis ? 'none' : (from[dir] || from.up),
      transition: `opacity 0.7s cubic-bezier(.16,1,.3,1) ${delay}s, transform 0.7s cubic-bezier(.16,1,.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
};

/* ─── STAT COUNTER ───────────────────────────── */

const Stat = ({ value, suffix, label, color, active }) => {
  const n = useCountUp(value, active);
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: 'clamp(36px,5vw,52px)', fontWeight: 800,
        color, fontFamily: "'Space Grotesk',sans-serif", lineHeight: 1,
        textShadow: `0 0 30px ${color}60`,
      }}>
        {n}{suffix}
      </div>
      <div style={{ color: '#6272a4', fontSize: 13, marginTop: 8, fontFamily: "'Space Grotesk',sans-serif", letterSpacing: '.5px' }}>
        {label}
      </div>
    </div>
  );
};

/* ─── SCROLL PROGRESS BAR ────────────────────── */

const ProgressBar = ({ pct }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, height: 2,
    zIndex: 200, pointerEvents: 'none',
    background: 'rgba(189,147,249,0.1)',
  }}>
    <div style={{
      height: '100%', width: `${pct}%`,
      background: 'linear-gradient(90deg,#bd93f9,#ff79c6,#50fa7b)',
      boxShadow: '0 0 8px rgba(189,147,249,0.8)',
      transition: 'width .1s linear',
    }} />
  </div>
);

/* ─── TEXT SCRAMBLE HOOK ─────────────────────── */

const useScramble = (text, active, speed = 28) => {
  const CHARS = '!<>_\\/[]{}—=+*^?#01ABCDEFabcdef♦♠♣';
  const [display, setDisplay] = useState(text);
  useEffect(() => {
    if (!active) return;
    let frame = 0;
    const steps = text.length * 3;
    const t = setInterval(() => {
      setDisplay(text.split('').map((ch, i) => {
        if (ch === ' ') return ' ';
        if (i < frame / 3) return ch;
        return CHARS[Math.floor(Math.random() * CHARS.length)];
      }).join(''));
      frame++;
      if (frame > steps) { setDisplay(text); clearInterval(t); }
    }, speed);
    return () => clearInterval(t);
  }, [active, text, speed]);
  return display;
};

/* ─── 3D TILT CARD ───────────────────────────── */

const TiltCard = ({ children, style = {}, intensity = 18 }) => {
  const ref = useRef(null);
  const raf = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, gx: 50, gy: 50, active: false });

  const onMove = e => {
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      setTilt({ rx: (y - .5) * -intensity, ry: (x - .5) * intensity, gx: x * 100, gy: y * 100, active: true });
    });
  };
  const onLeave = () => setTilt({ rx: 0, ry: 0, gx: 50, gy: 50, active: false });

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{
      ...style,
      transform: `perspective(700px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) scale(${tilt.active ? 1.04 : 1})`,
      transition: tilt.active ? 'transform .06s linear' : 'transform .5s cubic-bezier(.16,1,.3,1)',
      position: 'relative', transformStyle: 'preserve-3d',
    }}>
      {children}
      {/* Holographic glare */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none',
        background: `radial-gradient(circle at ${tilt.gx}% ${tilt.gy}%, rgba(255,255,255,.1) 0%, transparent 60%)`,
        opacity: tilt.active ? 1 : 0, transition: 'opacity .3s',
        zIndex: 2,
      }} />
    </div>
  );
};

/* ─── MAGNETIC BUTTON ────────────────────────── */

const MagneticButton = ({ children, onClick, style = {}, strength = 0.38 }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const raf = useRef(null);

  const onMove = e => {
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      setPos({ x: (e.clientX - cx) * strength, y: (e.clientY - cy) * strength });
    });
  };
  const onLeave = () => setPos({ x: 0, y: 0 });

  return (
    <button ref={ref} onClick={onClick} onMouseMove={onMove} onMouseLeave={onLeave} style={{
      ...style,
      transform: `translate(${pos.x}px, ${pos.y}px)`,
      transition: pos.x === 0 && pos.y === 0 ? 'transform .45s cubic-bezier(.16,1,.3,1)' : 'transform .08s linear',
    }}>
      {children}
    </button>
  );
};

/* ─── INTERACTIVE TERMINAL ───────────────────── */

const InteractiveTerminal = ({ onOpenForm }) => {
  const [lines, setLines] = useState([
    { t: 'sys', v: '🧛 DraculaDev Terminal v2.0.26 — Forged in Transylvania' },
    { t: 'sys', v: 'Tastează "help" pentru lista de comenzi disponibile.' },
  ]);
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);

  const CMDS = {
    help:     () => ['> Comenzi disponibile:', '  whoami · services · pricing · contact · hire · clear · easteregg'],
    whoami:   () => ['> DraculaDev — Web Development Studio', '  📍 Forged in Transylvania, România', '  ⚡ React · Django · AI · Full-Stack', '  🧛 Construim site-uri care nu mor niciodată.'],
    services: () => ['> Serviciile noastre:', '  🏰  Website-uri  (landing → e-commerce)', '  ⚡  Aplicații Web  (React, Django, Flask)', '  🎨  Design UI/UX', '  🔧  Mentenanță & Hosting'],
    pricing:  () => ['> Prețuri orientative:', '  🎯  Landing Page     → de la 1000 RON', '  📄  Site Prezentare  → de la 1300 RON', '  🛒  Magazin Online   → de la 3000 RON', '  🚀  App Custom       → de la 3500 RON', '  🛡️  Mentenanță       →      150 RON/lună'],
    contact:  () => ['> Contact:', '  📧  contact@draculadev.com', '  🏔️  Transilvania, România', '  ⏰  Răspuns în mai puțin de 24h'],
    hire:     () => ['> Se deschide formularul...', '__FORM__'],
    clear:    () => '__CLEAR__',
    easteregg:() => ['> 🎉 Ai găsit oul de Paște vampiric!', '  🧛🦇🕸️🏰🌙🧄💜', '  Vlad Țepeș aprobă acest terminal.'],
    vlad:     () => ['> 🦇 Vlad Țepeș a citit codul și nu a găsit bug-uri.', '  * bate din aripi aprobator *'],
    dracula:  () => ['> ⚡ EU SUNT DRACULA.', '  Codul tău îmi aparține acum.', '  ████████████████████████████████', '  ...Transylvania te cheamă.'],
    matrix:   () => ['> Wake up, Neo...', '  The Matrix has you.', '  🟩🟩🟩🟩🟩🟩🟩🟩🟩🟩', '  Follow the white rabbit. 🐰'],
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [lines]);

  const run = cmd => {
    const key = cmd.trim().toLowerCase();
    const fn = CMDS[key];
    if (!key) return;

    if (!fn) {
      setLines(p => [...p, { t: 'in', v: `$ ${cmd}` }, { t: 'err', v: `> bash: ${key}: command not found. Type "help".` }]);
      setInput(''); return;
    }

    const res = fn();
    if (res === '__CLEAR__') { setLines([]); setInput(''); return; }

    const filtered = Array.isArray(res) ? res.filter(r => r !== '__FORM__') : [];
    setLines(p => [...p, { t: 'in', v: `$ ${cmd}` }, ...filtered.map(v => ({ t: 'out', v }))]);
    setInput('');

    if (Array.isArray(res) && res.includes('__FORM__')) setTimeout(onOpenForm, 600);
  };

  const colorMap = { sys: '#6272a4', in: '#50fa7b', out: '#f8f8f2', err: '#ff5555' };

  return (
    <div
      onClick={() => { setFocused(true); inputRef.current?.focus(); }}
      style={{
        background: '#16181e', borderRadius: 14, maxWidth: 680, width: '100%',
        border: `1px solid ${focused ? 'rgba(189,147,249,.35)' : '#1e2030'}`,
        boxShadow: focused
          ? '0 24px 80px rgba(0,0,0,.7),0 0 0 1px rgba(189,147,249,.2),0 0 60px rgba(189,147,249,.12)'
          : '0 24px 80px rgba(0,0,0,.7),0 0 0 1px rgba(189,147,249,.08)',
        overflow: 'hidden', marginBottom: 48,
        transition: 'border-color .3s, box-shadow .3s',
        cursor: 'text',
      }}>
      {/* Title bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: '#111218', borderBottom: '1px solid #1e2030' }}>
        {['#ff5555','#f1fa8c','#50fa7b'].map((c,i) => <div key={i} style={{ width: 11, height: 11, borderRadius: '50%', background: c }} />)}
        <span style={{ marginLeft: 12, color: '#44475a', fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>
          dracula@dev ~ {focused ? '● INTERACTIV' : '○ click pentru a activa'}
        </span>
      </div>
      {/* Output */}
      <div style={{ padding: '16px 20px', maxHeight: 220, overflowY: 'auto', scrollbarWidth: 'thin' }}>
        {lines.map((l, i) => (
          <div key={i} style={{ color: colorMap[l.t] || '#f8f8f2', fontSize: 12, lineHeight: 1.8, fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'pre' }}>
            {l.v}
          </div>
        ))}
        {/* Current input line */}
        <div style={{ display: 'flex', alignItems: 'center', color: '#50fa7b', fontSize: 12, fontFamily: "'JetBrains Mono',monospace", marginTop: 4 }}>
          <span style={{ marginRight: 8 }}>$</span>
          <span style={{ color: '#f8f8f2' }}>{input}</span>
          <span style={{ animation: 'blink 1s step-end infinite', borderLeft: '2px solid #f8f8f2', marginLeft: 1, height: '1em', display: 'inline-block' }} />
        </div>
        <div ref={bottomRef} />
      </div>
      {/* Hidden input */}
      <input ref={inputRef} value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') run(input); if (e.key === 'Escape') setFocused(false); }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 1, height: 1 }}
        autoComplete="off" spellCheck={false}
      />
    </div>
  );
};

/* ─── EASTER EGG OVERLAY ─────────────────────── */

const EasterEgg = ({ onDone }) => {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99990, pointerEvents: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 16,
      background: 'rgba(5,0,10,.92)',
      animation: 'eggFade 3s ease forwards',
    }}>
      <style>{`@keyframes eggFade{0%{opacity:0}10%{opacity:1}80%{opacity:1}100%{opacity:0}}`}</style>
      <div style={{ fontSize: 'clamp(40px,8vw,80px)', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900, letterSpacing: '-2px', color: '#ff5555', textShadow: '0 0 40px #ff5555, 0 0 80px #bd93f9' }}>
        🧛 DRACULA 🧛
      </div>
      <div style={{ fontSize: 14, color: '#bd93f9', fontFamily: "'JetBrains Mono',monospace", letterSpacing: 4, textTransform: 'uppercase' }}>
        Transylvania te-a descoperit
      </div>
      <div style={{ fontSize: 36, letterSpacing: 8 }}>🦇🕸️🏰🌙🧄</div>
    </div>
  );
};

/* ─── SCRAMBLE TITLE ─────────────────────────── */

const ScrambleTitle = ({ text, color, prefix, style = {} }) => {
  const [ref, vis] = useInView(0.4);
  const scrambled = useScramble(text, vis, 22);
  return (
    <span ref={ref} style={style}>
      {prefix && <span style={{ color: '#44475a' }}>{prefix} </span>}
      <span style={{ color }}>{scrambled}</span>
    </span>
  );
};

/* ─── SCROLL INDICATOR ───────────────────────── */

const ScrollArrow = () => (
  <div style={{
    position: 'absolute', bottom: 32, left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 6, opacity: 0.45, animation: 'arrowBounce 2s ease-in-out infinite',
    pointerEvents: 'none',
  }}>
    <span style={{ color: '#6272a4', fontSize: 10, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2 }}>SCROLL</span>
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 3v14M4 12l6 6 6-6" stroke="#6272a4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </div>
);

/* ─── MAIN ───────────────────────────────────── */

const PresentationPage = ({ onSwitchToKids, onOpenForm }) => {
  const [visible, setVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [hovSvc, setHovSvc] = useState(null);
  const [hovPrice, setHovPrice] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [easterEgg, setEasterEgg] = useState(false);
  const keyBuffer = useRef('');
  const scrollProgress = useScrollProgress();
  const scrollY = useScrollY();
  const navSections = ['servicii', 'cum-functioneaza', 'ai-demo', 'preturi', 'contact'];
  const activeSection = useActiveSection(navSections);
  const [statsRef, statsVis] = useInView(0.3);

  const fullText = 'Construim experiențe digitale care nu mor niciodată.';
  const heroTitle = useScramble('cod nemuritor', visible, 30);

  useEffect(() => {
    setVisible(true);
    let i = 0;
    const t = setInterval(() => {
      if (i <= fullText.length) { setTypedText(fullText.slice(0, i)); i++; }
      else clearInterval(t);
    }, 50);
    return () => clearInterval(t);
  }, []);

  // Close mobile menu on scroll
  useEffect(() => { if (scrollY > 100) setMobileOpen(false); }, [scrollY]);

  // Easter egg: type DRACULA
  useEffect(() => {
    const onKey = e => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      keyBuffer.current = (keyBuffer.current + e.key.toLowerCase()).slice(-7);
      if (keyBuffer.current === 'dracula') { setEasterEgg(true); keyBuffer.current = ''; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const navScrolled = scrollY > 40;

  const navLinks = [
    { href: '#servicii', label: 'Servicii', id: 'servicii' },
    { href: '#cum-functioneaza', label: 'Cum funcționează', id: 'cum-functioneaza' },
    { href: '#preturi', label: 'Prețuri', id: 'preturi' },
    { href: '#contact', label: 'Contact', id: 'contact' },
  ];

  const sideNavSections = [
    { id: 'hero',            label: 'Acasă',           color: '#bd93f9' },
    { id: 'servicii',        label: 'Servicii',         color: '#ff79c6' },
    { id: 'cum-functioneaza',label: 'Cum funcționează', color: '#50fa7b' },
    { id: 'ai-demo',         label: 'AI Demo',          color: '#8be9fd' },
    { id: 'preturi',         label: 'Prețuri',          color: '#f1fa8c' },
    { id: 'contact',         label: 'Contact',          color: '#50fa7b' },
  ];

  const services = [
    { icon: '🏰', title: 'Website-uri', desc: 'De la landing page la e-commerce. Fiecare site e o fortăreață digitală.', slug: 'website-uri', color: '#50fa7b' },
    { icon: '⚡', title: 'Aplicații Web', desc: 'React, Django, Flask — stiva completă, putere completă.', slug: 'aplicatii-web', color: '#ff79c6' },
    { icon: '🎨', title: 'Design UI/UX', desc: 'Interfețe care captivează. Dark mode? Întotdeauna.', slug: 'design-ui-ux', color: '#bd93f9' },
    { icon: '🔧', title: 'Mentenanță', desc: 'Site-ul tău nu doarme niciodată. Nici noi.', slug: 'mentenanta', color: '#f1fa8c' },
  ];

  const steps = [
    { num: '01', icon: '📝', title: 'Descrii afacerea', desc: 'Completezi un formular simplu cu detalii despre business-ul tău. Durează 2 minute.', color: '#50fa7b' },
    { num: '02', icon: '🧄', title: 'AI-ul generează 3 demo-uri', desc: 'În 30 de secunde primești 3 variante de website, fiecare cu stil diferit.', color: '#ff79c6' },
    { num: '03', icon: '🚀', title: 'Alegi și lansăm', desc: 'Selectezi varianta preferată, o personalizăm împreună și lansăm site-ul.', color: '#bd93f9' },
  ];

  const trustPoints = [
    { icon: '💰', text: 'Prețuri accesibile pentru România' },
    { icon: '✍️', text: 'Cod scris manual, nu template WordPress' },
    { icon: '🤖', text: 'AI integrat în procesul de dezvoltare' },
    { icon: '🇷🇴', text: 'Suport în română, comunicare directă' },
    { icon: '⚡', text: 'Livrare rapidă: 2-4 săptămâni' },
    { icon: '🔒', text: 'SSL gratuit, hosting securizat' },
  ];

  const prices = [
    { title: 'Landing Page', price: '1.000', unit: 'RON', desc: 'O pagină, impact maxim', icon: '🎯', color: '#50fa7b', popular: false },
    { title: 'Site Prezentare', price: '1.300', unit: 'RON', desc: 'Multi-pagină profesional', icon: '📄', color: '#8be9fd', popular: true },
    { title: 'Magazin Online', price: '3.000', unit: 'RON', desc: 'E-commerce complet', icon: '🛒', color: '#ff79c6', popular: false },
    { title: 'Aplicație Custom', price: '3.500', unit: 'RON', desc: 'Full-stack pe măsură', icon: '🚀', color: '#bd93f9', popular: false },
    { title: 'Mentenanță', price: '150', unit: 'RON/lună', desc: 'Grijă continuă', icon: '🛡️', color: '#f1fa8c', popular: false },
  ];

  const sp = { padding: '100px 24px', position: 'relative', zIndex: 10 };
  const si = { maxWidth: 1000, margin: '0 auto' };
  const sTitle = {
    textAlign: 'center', fontSize: 'clamp(24px,4vw,32px)', marginBottom: 16,
    fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
  };
  const sSub = {
    textAlign: 'center', color: '#6272a4', fontSize: 15,
    maxWidth: 550, margin: '0 auto 56px', lineHeight: 1.6,
    fontFamily: "'Space Grotesk',sans-serif",
  };

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', color: '#f8f8f2', fontFamily: "'JetBrains Mono',monospace", position: 'relative', overflow: 'hidden' }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

        @keyframes blink { 0%,100%{opacity:1}50%{opacity:0} }
        @keyframes gradientText { 0%,100%{background-position:0% 50%}50%{background-position:100% 50%} }
        @keyframes orbFloat { 0%,100%{transform:translate(0,0) scale(1);opacity:.12}33%{transform:translate(40px,-50px) scale(1.12);opacity:.2}66%{transform:translate(-25px,30px) scale(.92);opacity:.09} }
        @keyframes float { 0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)} }
        @keyframes ctaGlow { 0%,100%{box-shadow:0 0 40px rgba(255,121,198,.2),0 0 100px rgba(189,147,249,.1)}50%{box-shadow:0 0 80px rgba(255,121,198,.45),0 0 160px rgba(189,147,249,.25)} }
        @keyframes arrowBounce { 0%,100%{transform:translateX(-50%) translateY(0)}50%{transform:translateX(-50%) translateY(8px)} }
        @keyframes slideIn { from{transform:scaleX(0);opacity:0}to{transform:scaleX(1);opacity:1} }
        @keyframes menuSlide { from{transform:translateY(-8px);opacity:0}to{transform:translateY(0);opacity:1} }
        @keyframes shimmer { 0%{background-position:-200% 0}100%{background-position:200% 0} }
        @keyframes scanLine { 0%{background-position:0 0}100%{background-position:0 60px} }
        @keyframes borderRotate { 0%{background-position:0% 50%}100%{background-position:200% 50%} }

        .nav-link-item { position:relative; font-family:'Space Grotesk',sans-serif; font-size:13px; font-weight:500; color:#6272a4; text-decoration:none; padding:4px 0; transition:color .2s; letter-spacing:.3px; }
        .nav-link-item::after { content:''; position:absolute; bottom:-2px; left:0; width:0; height:1px; background:linear-gradient(90deg,#bd93f9,#ff79c6); transition:width .25s cubic-bezier(.16,1,.3,1); }
        .nav-link-item:hover { color:#f8f8f2; }
        .nav-link-item:hover::after { width:100%; }
        .nav-link-item.active { color:#f8f8f2; }
        .nav-link-item.active::after { width:100%; }

        .step-card:hover { transform:translateY(-8px) !important; box-shadow:0 24px 60px rgba(0,0,0,.5) !important; }
        .trust-item:hover { border-color:#8be9fd30 !important; background:#8be9fd05 !important; transform:translateX(4px); }
        .trust-item { transition:all .25s !important; }

        @media(max-width:860px) { .nav-links-desktop{display:none!important} .nav-hamburger{display:flex!important} }
        @media(min-width:861px) { .nav-hamburger{display:none!important} .mobile-menu{display:none!important} }
        @media(max-width:768px) { .steps-grid{grid-template-columns:1fr!important} .prices-grid{grid-template-columns:repeat(2,1fr)!important} .trust-grid{grid-template-columns:1fr!important} .stats-grid{grid-template-columns:repeat(2,1fr)!important} .contact-grid{grid-template-columns:1fr!important} }
        @media(max-width:480px) { .prices-grid{grid-template-columns:1fr!important} .stats-grid{grid-template-columns:repeat(2,1fr)!important} }
        @media(max-width:600px) { .hero-btns{flex-direction:column;align-items:stretch!important} }
      `}</style>

      {easterEgg && <EasterEgg onDone={() => setEasterEgg(false)} />}
      <AuroraBackground />
      <StarField />
      <ParticleNet />
      <ProgressBar pct={scrollProgress} />
      <CustomCursor />
      <FlyingBat delay={1000} startX={-50} startY={80} size={20} />
      <FlyingBat delay={4000} startX={-80} startY={200} size={16} />
      <FlyingBat delay={7000} startX={-30} startY={350} size={22} />
      <SideNav sections={sideNavSections} active={activeSection} />

      {/* Ambient orbs — parallax on scroll, above overlay */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 5, overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: '8%', left: '12%',
          width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(189,147,249,.13) 0%,transparent 70%)',
          filter: 'blur(60px)',
          animation: 'orbFloat 14s ease-in-out infinite',
          transform: `translateY(${scrollY * 0.12}px)`,
        }} />
        <div style={{
          position: 'absolute', top: '45%', right: '8%',
          width: 450, height: 450, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(255,121,198,.11) 0%,transparent 70%)',
          filter: 'blur(55px)',
          animation: 'orbFloat 18s ease-in-out 5s infinite',
          transform: `translateY(${scrollY * -0.08}px)`,
        }} />
        <div style={{
          position: 'absolute', bottom: '10%', left: '25%',
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(80,250,123,.09) 0%,transparent 70%)',
          filter: 'blur(50px)',
          animation: 'orbFloat 22s ease-in-out 10s infinite',
          transform: `translateY(${scrollY * 0.06}px)`,
        }} />
      </div>

      {/* Thin dark tint — keeps text readable without killing aurora */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 4, background: 'rgba(6,8,16,.42)', pointerEvents: 'none' }} />

      {/* ══════════════════════════════════════
          NAV
      ══════════════════════════════════════ */}
      <nav style={{
        position: 'fixed', top: 2, left: 0, right: 0, zIndex: 100,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 32px',
        background: navScrolled ? 'rgba(10,11,15,.95)' : 'rgba(13,14,18,.75)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${navScrolled ? 'rgba(189,147,249,.18)' : 'transparent'}`,
        transition: 'all .35s ease',
        boxShadow: navScrolled ? '0 4px 30px rgba(0,0,0,.4)' : 'none',
      }}>

        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }}>
          <span style={{ fontSize: 24 }}>🧛</span>
          <span style={{
            fontSize: 18, fontWeight: 700,
            background: 'linear-gradient(135deg,#bd93f9,#ff79c6)',
            backgroundSize: '200% 200%', animation: 'gradientText 4s ease infinite',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            fontFamily: "'Space Grotesk',sans-serif",
          }}>DraculaDev</span>
          <span style={{ color: '#50fa7b', fontSize: 10, opacity: .5, fontFamily: "'JetBrains Mono',monospace" }}>.com</span>
        </Link>

        {/* Desktop nav links with sliding indicator */}
        <div className="nav-links-desktop" style={{ display: 'flex', gap: 28, alignItems: 'center', position: 'relative' }}>
          {navLinks.map(l => {
            const isActive = activeSection === l.id;
            return (
              <a key={l.id} href={l.href} className={`nav-link-item${isActive ? ' active' : ''}`}
                style={{ position: 'relative' }}>
                {l.label}
                {isActive && (
                  <span style={{
                    position: 'absolute', bottom: -4, left: 0, right: 0,
                    height: 2, borderRadius: 1,
                    background: 'linear-gradient(90deg,#bd93f9,#ff79c6)',
                    boxShadow: '0 0 8px rgba(189,147,249,.8)',
                    animation: 'slideIn .3s cubic-bezier(.16,1,.3,1)',
                  }} />
                )}
              </a>
            );
          })}
        </div>

        {/* Desktop CTA */}
        <div className="nav-links-desktop" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={onOpenForm} style={{
            background: 'transparent', border: '1px solid #50fa7b40',
            borderRadius: 8, padding: '7px 16px',
            color: '#50fa7b', fontFamily: "'JetBrains Mono',monospace",
            fontWeight: 500, fontSize: 12, cursor: 'pointer', transition: 'all .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#50fa7b15'; e.currentTarget.style.borderColor = '#50fa7b'; e.currentTarget.style.boxShadow = '0 0 20px rgba(80,250,123,.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#50fa7b40'; e.currentTarget.style.boxShadow = 'none'; }}>
            Demo Gratuit
          </button>
          <button onClick={onSwitchToKids} style={{
            background: 'linear-gradient(135deg,#ff79c6,#bd93f9)',
            border: 'none', borderRadius: 8, padding: '8px 18px',
            color: '#fff', fontFamily: "'Space Grotesk',sans-serif",
            fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,121,198,.35)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
            🎮 Cursuri Copii
          </button>
        </div>

        {/* Hamburger */}
        <button
          className="nav-hamburger"
          onClick={() => setMobileOpen(o => !o)}
          style={{
            background: 'transparent', border: '1px solid #1e2030',
            borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center',
          }}>
          {[0, 1, 2].map(i => (
            <span key={i} style={{
              display: 'block', width: 20, height: 1.5,
              background: mobileOpen ? (i === 1 ? 'transparent' : '#bd93f9') : '#a0a4b8',
              borderRadius: 2, transition: 'all .25s',
              transform: mobileOpen
                ? i === 0 ? 'rotate(45deg) translate(4px,4px)' : i === 2 ? 'rotate(-45deg) translate(4px,-4px)' : 'none'
                : 'none',
            }} />
          ))}
        </button>
      </nav>

      {/* Mobile menu dropdown */}
      {mobileOpen && (
        <div className="mobile-menu" style={{
          position: 'fixed', top: 58, left: 0, right: 0, zIndex: 99,
          background: 'rgba(10,11,15,.98)', backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(189,147,249,.2)',
          padding: '20px 24px 24px',
          animation: 'menuSlide .2s ease',
          boxShadow: '0 20px 40px rgba(0,0,0,.6)',
        }}>
          {navLinks.map(l => (
            <a key={l.id} href={l.href}
               onClick={() => setMobileOpen(false)}
               style={{
                 display: 'block', padding: '12px 0',
                 borderBottom: '1px solid #1e2030',
                 color: activeSection === l.id ? '#f8f8f2' : '#6272a4',
                 fontFamily: "'Space Grotesk',sans-serif",
                 fontSize: 15, fontWeight: 500, textDecoration: 'none',
                 transition: 'color .2s',
               }}>
              {l.label}
            </a>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={() => { setMobileOpen(false); onOpenForm(); }} style={{
              flex: 1, background: 'linear-gradient(135deg,#50fa7b,#8be9fd)',
              border: 'none', borderRadius: 8, padding: '12px',
              color: '#0d0e12', fontFamily: "'Space Grotesk',sans-serif",
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}>⚡ Demo Gratuit</button>
            <button onClick={() => { setMobileOpen(false); onSwitchToKids(); }} style={{
              flex: 1, background: 'linear-gradient(135deg,#ff79c6,#bd93f9)',
              border: 'none', borderRadius: 8, padding: '12px',
              color: '#fff', fontFamily: "'Space Grotesk',sans-serif",
              fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}>🎮 Cursuri Copii</button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section id="hero" style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center',
        padding: '120px 24px 80px', textAlign: 'center',
        position: 'relative', zIndex: 10,
        background: `radial-gradient(ellipse at 50% 0%,rgba(189,147,249,.18) 0%,transparent 60%),
                     radial-gradient(ellipse at 20% 80%,rgba(80,250,123,.1) 0%,transparent 40%),
                     radial-gradient(ellipse at 80% 50%,rgba(255,121,198,.08) 0%,transparent 40%)`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 1s ease',
      }}>
        <InteractiveTerminal onOpenForm={onOpenForm} />

        <h1 style={{
          fontSize: 'clamp(34px,5.5vw,60px)', fontWeight: 800,
          lineHeight: 1.08, marginBottom: 20,
          fontFamily: "'Space Grotesk',sans-serif", letterSpacing: '-.02em',
        }}>
          <span style={{ color: '#f8f8f2' }}>Transformăm idei în </span><br />
          <span style={{
            background: 'linear-gradient(135deg,#bd93f9,#ff79c6,#50fa7b,#8be9fd)',
            backgroundSize: '300% 300%', animation: 'gradientText 6s ease infinite',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            fontVariantNumeric: 'tabular-nums',
          }}>{heroTitle}</span>
        </h1>

        <p style={{ color: '#6272a4', fontSize: 16, maxWidth: 480, lineHeight: 1.7, marginBottom: 40, fontFamily: "'Space Grotesk',sans-serif" }}>
          Web development din inima Transilvaniei. Site-uri, aplicații web și soluții digitale care lucrează non-stop.
        </p>

        <div className="hero-btns" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
          <MagneticButton onClick={onOpenForm} style={{
            background: 'linear-gradient(135deg,#50fa7b,#8be9fd)',
            border: 'none', borderRadius: 10, padding: '15px 36px',
            color: '#0d0e12', fontFamily: "'Space Grotesk',sans-serif",
            fontWeight: 700, fontSize: 15, cursor: 'pointer',
            boxShadow: '0 0 35px rgba(80,250,123,.35),0 0 80px rgba(139,233,253,.15)',
          }}>
            ⚡ Descrie afacerea ta
          </MagneticButton>
          <a href="#preturi" style={{
            padding: '15px 28px', borderRadius: 10, border: '1px solid #44475a',
            background: 'transparent', color: '#a0a4b8',
            fontFamily: "'Space Grotesk',sans-serif", fontWeight: 500, fontSize: 14,
            cursor: 'pointer', textDecoration: 'none', transition: 'all .2s',
            display: 'flex', alignItems: 'center',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#bd93f9'; e.currentTarget.style.color = '#f8f8f2'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#44475a'; e.currentTarget.style.color = '#a0a4b8'; }}>
            Vezi prețuri ↓
          </a>
        </div>

        <ScrollArrow />
      </section>

      {/* ══════════════════════════════════════
          SERVICII
      ══════════════════════════════════════ */}
      <section id="servicii" style={{ ...sp, background: 'linear-gradient(180deg,transparent,rgba(189,147,249,.02),transparent)' }}>
        <div style={si}>
          <FadeIn>
            <h2 style={sTitle}>
              <ScrambleTitle text="Ce putem face pentru tine" color="#ff79c6" prefix="//" /></h2>
            <p style={sSub}>De la landing page la sisteme enterprise — tot ce ai nevoie pentru prezența ta digitală.</p>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 16 }}>
            {services.map((s, i) => (
              <FadeIn key={i} delay={0.1 * i} dir="up">
                <Link to={`/servicii/${s.slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  <TiltCard style={{ borderRadius: 14 }}>
                    <div style={{
                      background: '#13141a', border: `1px solid #1e2030`,
                      borderRadius: 14, padding: '28px 24px', cursor: 'pointer',
                      boxShadow: `0 4px 20px rgba(0,0,0,.4)`,
                      transition: 'border-color .3s, box-shadow .3s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = s.color + '70'; e.currentTarget.style.boxShadow = `0 24px 70px ${s.color}30`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2030'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,.4)'; }}>
                      <div style={{ fontSize: 34, marginBottom: 16 }}>{s.icon}</div>
                      <h3 style={{ color: s.color, fontSize: 17, marginBottom: 10, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600 }}>{s.title}</h3>
                      <p style={{ color: '#6272a4', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>{s.desc}</p>
                      <span style={{ color: s.color, fontSize: 12, opacity: .5, fontFamily: "'JetBrains Mono',monospace" }}>
                        vezi detalii →
                      </span>
                    </div>
                  </TiltCard>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <GeoDivider color1="#ff79c6" color2="#bd93f9" />

      {/* ══════════════════════════════════════
          CUM FUNCȚIONEAZĂ
      ══════════════════════════════════════ */}
      <section id="cum-functioneaza" style={sp}>
        <div style={si}>
          <FadeIn>
            <h2 style={sTitle}><ScrambleTitle text="Cum funcționează" color="#50fa7b" prefix="$" /></h2>
            <p style={sSub}>3 pași simpli de la descriere la website funcțional. AI-ul nostru face magia.</p>
          </FadeIn>

          <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
            {steps.map((step, i) => (
              <FadeIn key={i} delay={0.15 * i}>
                <div className="step-card" style={{
                  background: 'linear-gradient(145deg,#13141a,#0f1015)',
                  border: '1px solid #1e2030',
                  borderRadius: 16, padding: '36px 28px', textAlign: 'center',
                  position: 'relative', overflow: 'hidden',
                  boxShadow: '0 8px 30px rgba(0,0,0,.4)',
                  transition: 'all .35s cubic-bezier(.16,1,.3,1)',
                  cursor: 'default',
                }}>
                  {/* Top accent line */}
                  <div style={{
                    position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                    width: '60%', height: 2,
                    background: `linear-gradient(90deg,transparent,${step.color},${step.color},transparent)`,
                    boxShadow: `0 0 16px ${step.color}`,
                  }} />
                  {/* Step number badge */}
                  <div style={{
                    display: 'inline-block', background: `${step.color}12`,
                    border: `1px solid ${step.color}30`,
                    borderRadius: 6, padding: '2px 10px', marginBottom: 16,
                    fontSize: 10, color: step.color, fontWeight: 700,
                    fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2,
                  }}>PASUL {step.num}</div>
                  <div style={{ fontSize: 44, marginBottom: 16, animation: `float 3s ease-in-out ${i * 0.5}s infinite` }}>{step.icon}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10, fontFamily: "'Space Grotesk',sans-serif", color: '#f8f8f2' }}>{step.title}</h3>
                  <p style={{ color: '#6272a4', fontSize: 13, lineHeight: 1.6 }}>{step.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <GeoDivider color1="#50fa7b" color2="#8be9fd" flip />

      {/* ══════════════════════════════════════
          AI DEMO SECTION
      ══════════════════════════════════════ */}
      <section id="ai-demo" style={{ ...sp, background: 'radial-gradient(ellipse at 50% 50%,rgba(255,121,198,.04) 0%,transparent 60%)' }}>
        <div style={{ ...si, maxWidth: 800 }}>
          <FadeIn>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🧄</div>
              <h2 style={{
                ...sTitle,
                background: 'linear-gradient(135deg,#ff79c6,#bd93f9)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                display: 'inline-block',
              }}>AI-ul nostru din Transilvania</h2>
              <p style={{ ...sSub, marginBottom: 40 }}>
                Generăm 3 variante de website demo gratuit, personalizate pentru afacerea ta. Fără obligații, fără costuri ascunse. 30 de secunde, 3 stiluri diferite.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16, marginBottom: 40 }}>
              {[
                { label: 'Modern & Minimalist', color: '#50fa7b', icon: '🎨', desc: 'Clean, airy, sans-serif' },
                { label: 'Bold & Dark', color: '#ff79c6', icon: '🎆', desc: 'Glassmorphism, animații' },
                { label: 'Classic & Elegant', color: '#bd93f9', icon: '🏛️', desc: 'Serif, warm, timeless' },
              ].map((demo, i) => (
                <div key={i} style={{
                  background: '#13141a', border: `1px solid ${demo.color}20`,
                  borderRadius: 12, padding: '24px 20px', textAlign: 'center', transition: 'all .3s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = demo.color + '60'; e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = `0 16px 40px ${demo.color}18`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = demo.color + '20'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>{demo.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: demo.color, marginBottom: 6, fontFamily: "'Space Grotesk',sans-serif" }}>{demo.label}</div>
                  <div style={{ fontSize: 11, color: '#44475a', fontFamily: "'JetBrains Mono',monospace" }}>{demo.desc}</div>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.35}>
            <div style={{ textAlign: 'center' }}>
              <button onClick={onOpenForm} style={{
                background: 'linear-gradient(135deg,#ff79c6,#bd93f9)',
                border: 'none', borderRadius: 10, padding: '15px 40px',
                color: '#fff', fontFamily: "'Space Grotesk',sans-serif",
                fontWeight: 700, fontSize: 15, cursor: 'pointer',
                boxShadow: '0 0 30px rgba(255,121,198,.2)', transition: 'all .25s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.03)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(255,121,198,.45)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 0 30px rgba(255,121,198,.2)'; }}>
                🧛 Generează Demo Gratuit →
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════
          DE CE DRACULADEV
      ══════════════════════════════════════ */}
      <section style={sp}>
        <div style={si}>
          <FadeIn>
            <h2 style={sTitle}><ScrambleTitle text="De ce DraculaDev?" color="#8be9fd" prefix="#" /></h2>
            <p style={sSub}>Nu suntem o corporație fără chip. Suntem dezvoltatori care vorbesc limba ta.</p>
          </FadeIn>

          <div className="trust-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
            {trustPoints.map((point, i) => (
              <FadeIn key={i} delay={0.07 * i}>
                <div className="trust-item" style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  background: '#13141a', border: '1px solid #1e2030',
                  borderRadius: 12, padding: '16px 20px',
                }}>
                  <span style={{ fontSize: 24, flexShrink: 0 }}>{point.icon}</span>
                  <span style={{ color: '#a0a4b8', fontSize: 14, fontWeight: 500, fontFamily: "'Space Grotesk',sans-serif" }}>{point.text}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          STATS (COUNTER SECTION)
      ══════════════════════════════════════ */}
      <section style={{
        ...sp, paddingTop: 0,
        background: 'linear-gradient(180deg,transparent,rgba(80,250,123,.02),transparent)',
      }}>
        <div style={si}>
          <FadeIn>
            <div style={{
              background: 'linear-gradient(135deg,rgba(189,147,249,.05),rgba(80,250,123,.03))',
              border: '1px solid rgba(189,147,249,.15)',
              borderRadius: 20, padding: '48px 32px',
            }}>
              <div ref={statsRef} className="stats-grid" style={{
                display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 32,
              }}>
                <Stat value={50}  suffix="+"  label="Clienți fericiți"     color="#50fa7b" active={statsVis} />
                <Stat value={4}   suffix="+"  label="Ani experiență"       color="#8be9fd" active={statsVis} />
                <Stat value={30}  suffix="s"  label="Demo generat"         color="#ff79c6" active={statsVis} />
                <Stat value={100} suffix="%"  label="Satisfacție garantată" color="#bd93f9" active={statsVis} />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <GeoDivider color1="#bd93f9" color2="#f1fa8c" />

      {/* ══════════════════════════════════════
          PREȚURI
      ══════════════════════════════════════ */}
      <section id="preturi" style={{ ...sp, background: 'radial-gradient(ellipse at 50% 30%,rgba(80,250,123,.03) 0%,transparent 50%)' }}>
        <div style={si}>
          <FadeIn>
            <h2 style={sTitle}><ScrambleTitle text="Prețuri orientative" color="#f1fa8c" prefix=">" /></h2>
            <p style={sSub}>Transparent, fără surprize. Prețuri de la, personalizate în funcție de complexitate.</p>
          </FadeIn>

          <div className="prices-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16 }}>
            {prices.map((p, i) => (
              <FadeIn key={i} delay={0.1 * i} dir="scale">
                <div
                  onMouseEnter={() => setHovPrice(i)}
                  onMouseLeave={() => setHovPrice(null)}
                  style={{
                    background: p.popular
                      ? `linear-gradient(145deg,${p.color}08,#13141a)`
                      : hovPrice === i ? `${p.color}07` : '#13141a',
                    border: `1px solid ${p.popular ? p.color + '60' : hovPrice === i ? p.color + '45' : '#1e2030'}`,
                    borderRadius: 14, padding: '28px 22px', textAlign: 'center',
                    transition: 'all .3s', position: 'relative',
                    transform: hovPrice === i ? 'translateY(-8px)' : 'none',
                    boxShadow: p.popular
                      ? `0 0 40px ${p.color}25,0 0 80px ${p.color}0a`
                      : hovPrice === i ? `0 20px 50px ${p.color}22` : '0 4px 16px rgba(0,0,0,.3)',
                  }}>
                  {p.popular && (
                    <div style={{
                      position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                      background: p.color, color: '#0d0e12', fontSize: 10, fontWeight: 700,
                      padding: '3px 14px', borderRadius: 10, fontFamily: "'JetBrains Mono',monospace",
                    }}>POPULAR</div>
                  )}
                  <div style={{ fontSize: 32, marginBottom: 12 }}>{p.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#f8f8f2', marginBottom: 8, fontFamily: "'Space Grotesk',sans-serif" }}>{p.title}</div>
                  <div style={{ fontSize: 11, color: '#44475a', marginBottom: 16, fontFamily: "'JetBrains Mono',monospace" }}>{p.desc}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4 }}>
                    <span style={{ fontSize: 11, color: '#6272a4' }}>de la</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: p.color, fontFamily: "'Space Grotesk',sans-serif" }}>{p.price}</span>
                    <span style={{ fontSize: 11, color: '#6272a4', fontFamily: "'JetBrains Mono',monospace" }}>{p.unit}</span>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          CTA
      ══════════════════════════════════════ */}
      <section style={{ ...sp, paddingBottom: 60 }}>
        <FadeIn dir="scale">
          <div style={{
            background: 'linear-gradient(135deg,rgba(189,147,249,.1),rgba(255,121,198,.07),rgba(80,250,123,.04))',
            border: '1px solid rgba(189,147,249,.3)',
            borderRadius: 20, padding: '56px 32px', maxWidth: 620, margin: '0 auto',
            textAlign: 'center', animation: 'ctaGlow 5s ease-in-out infinite',
          }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🦇</div>
            <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 26, marginBottom: 12, fontWeight: 700 }}>
              Ai o afacere fără website?
            </h2>
            <p style={{ color: '#6272a4', marginBottom: 28, fontSize: 15, lineHeight: 1.6, fontFamily: "'Space Grotesk',sans-serif" }}>
              Spune-ne despre afacerea ta și primești 3 variante demo gratuit.<br />
              Fără obligații, fără colți ascunși.
            </p>
            <button onClick={onOpenForm} style={{
              background: 'linear-gradient(135deg,#ff79c6,#bd93f9)',
              border: 'none', borderRadius: 10, padding: '15px 36px',
              color: '#fff', fontFamily: "'Space Grotesk',sans-serif",
              fontWeight: 700, fontSize: 15, cursor: 'pointer',
              boxShadow: '0 0 25px rgba(255,121,198,.2)', transition: 'all .25s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.04)'; e.currentTarget.style.boxShadow = '0 10px 40px rgba(255,121,198,.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 0 25px rgba(255,121,198,.2)'; }}>
              🧛 Descrie afacerea ta →
            </button>
          </div>
        </FadeIn>
      </section>

      {/* ══════════════════════════════════════
          CONTACT SECTION
      ══════════════════════════════════════ */}
      <section id="contact" style={{ ...sp, paddingTop: 60, paddingBottom: 60 }}>
        <div style={si}>
          <FadeIn>
            <h2 style={sTitle}>
              <span style={{ color: '#44475a' }}>@ </span>
              <span style={{ color: '#50fa7b' }}>Contact</span>
            </h2>
            <p style={sSub}>Suntem la un email distanță. Fără formulare complicate.</p>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 700, margin: '0 auto' }}>
              {[
                { icon: '📧', label: 'Email', value: 'contact@draculadev.com', href: 'mailto:contact@draculadev.com', color: '#8be9fd' },
                { icon: '🏔️', label: 'Locație', value: 'Transilvania, România', href: null, color: '#50fa7b' },
                { icon: '⚡', label: 'Timp răspuns', value: 'Sub 24 de ore', href: null, color: '#ff79c6' },
                { icon: '🧛', label: 'Program', value: 'Non-stop (vampiric)', href: null, color: '#bd93f9' },
              ].map((item, i) => (
                <div key={i} style={{
                  background: '#13141a', border: '1px solid #1e2030',
                  borderRadius: 14, padding: '22px 24px',
                  display: 'flex', alignItems: 'center', gap: 16,
                  transition: 'all .25s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = item.color + '40'; e.currentTarget.style.background = item.color + '06'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2030'; e.currentTarget.style.background = '#13141a'; }}>
                  <span style={{ fontSize: 26, flexShrink: 0 }}>{item.icon}</span>
                  <div>
                    <div style={{ color: '#44475a', fontSize: 10, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>{item.label}</div>
                    {item.href
                      ? <a href={item.href} style={{ color: item.color, fontSize: 14, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 500, textDecoration: 'none' }}>{item.value}</a>
                      : <span style={{ color: '#f8f8f2', fontSize: 14, fontFamily: "'Space Grotesk',sans-serif", fontWeight: 500 }}>{item.value}</span>
                    }
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.3}>
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <a href="mailto:contact@draculadev.com?subject=Vreau%20un%20site%20-%20DraculaDev" style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg,#50fa7b,#8be9fd)',
                borderRadius: 10, padding: '13px 32px',
                color: '#0d0e12', fontFamily: "'Space Grotesk',sans-serif",
                fontWeight: 700, fontSize: 14, textDecoration: 'none',
                boxShadow: '0 0 25px rgba(80,250,123,.25)', transition: 'all .2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(80,250,123,.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 0 25px rgba(80,250,123,.25)'; }}>
                📧 Scrie-ne un email →
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FOOTER
      ══════════════════════════════════════ */}
      <footer style={{ position: 'relative', zIndex: 10 }}>
        <CastleSilhouette color="rgba(10,11,15,.95)" />
        <div style={{ background: 'rgba(10,11,15,.95)', padding: '40px 24px 24px', borderTop: '1px solid #1a1c28' }}>
          <div style={{
            maxWidth: 1000, margin: '0 auto',
            display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
            alignItems: 'flex-start', gap: 32,
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 22 }}>🧛</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#f8f8f2', fontFamily: "'Space Grotesk',sans-serif" }}>DraculaDev</span>
              </div>
              <div style={{ color: '#44475a', fontSize: 12, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.8 }}>
                contact@draculadev.com<br />
                Forged in Transylvania 🏔️
              </div>
            </div>
            <div>
              <div style={{ color: '#6272a4', fontSize: 11, fontWeight: 600, marginBottom: 12, letterSpacing: 1, fontFamily: "'JetBrains Mono',monospace" }}>SERVICII</div>
              {services.map((s, i) => (
                <Link key={i} to={`/servicii/${s.slug}`} style={{
                  display: 'block', color: '#44475a', textDecoration: 'none',
                  fontSize: 12, marginBottom: 8, transition: 'color .2s',
                  fontFamily: "'Space Grotesk',sans-serif",
                }}
                onMouseEnter={e => e.target.style.color = s.color}
                onMouseLeave={e => e.target.style.color = '#44475a'}>
                  {s.icon} {s.title}
                </Link>
              ))}
            </div>
            <div>
              <div style={{ color: '#6272a4', fontSize: 11, fontWeight: 600, marginBottom: 12, letterSpacing: 1, fontFamily: "'JetBrains Mono',monospace" }}>NAVIGARE</div>
              {navLinks.map(l => (
                <a key={l.id} href={l.href} style={{
                  display: 'block', color: '#44475a', textDecoration: 'none',
                  fontSize: 12, marginBottom: 8, transition: 'color .2s',
                  fontFamily: "'Space Grotesk',sans-serif",
                }}
                onMouseEnter={e => e.target.style.color = '#bd93f9'}
                onMouseLeave={e => e.target.style.color = '#44475a'}>
                  {l.label}
                </a>
              ))}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #1a1c28', marginTop: 32, paddingTop: 16, textAlign: 'center' }}>
            <span style={{ color: '#2a2d3a', fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>
              © 2026 DraculaDev.com — Forged in Transylvania 🧛
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PresentationPage;
